import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import BrandWordmark from './BrandWordmark';
import PublicSiteFooter from './PublicSiteFooter';
import { submitContactForm } from '../utils/apiUtils';
import './InfoPages.css';

const INITIAL_FORM = {
  name: '',
  email: '',
  reason: '',
  subject: '',
  message: '',
};

const CONTACT_EMAILS = {
  'Sales & Enterprise': 'sales@redacto.com',
  'Press & Media': 'press@redacto.com',
  'General Inquiry': 'info@redacto.net',
};

function Contact({ theme = 'dark', toggleTheme }) {
  const navigate = useNavigate();
  const isDark = theme === 'dark';
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitState, setSubmitState] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const targetEmail = useMemo(
    () => CONTACT_EMAILS[formData.reason] || 'info@redacto.net',
    [formData.reason]
  );

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: '' }));
    setSubmitState({ type: '', message: '' });
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.name.trim()) nextErrors.name = 'Please enter your full name.';
    if (!formData.email.trim()) {
      nextErrors.email = 'Please enter your email address.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = 'Please enter a valid email address.';
    }
    if (!formData.reason) nextErrors.reason = 'Please select a reason for contact.';
    if (!formData.subject.trim()) nextErrors.subject = 'Please add a subject.';
    if (!formData.message.trim()) {
      nextErrors.message = 'Please include a short message.';
    } else if (formData.message.trim().length < 20) {
      nextErrors.message = 'Please add a little more detail so we can help.';
    }

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validateForm();

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setSubmitState({
        type: 'error',
        message: 'Please fix the highlighted fields and try again.',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await submitContactForm({
        name: formData.name.trim(),
        email: formData.email.trim(),
        reason: formData.reason,
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        target_email: targetEmail,
      });

      if (!result.success) {
        throw new Error(result.error || 'Something went wrong while sending your message.');
      }

      setSubmitState({
        type: 'success',
        message: 'Your message has been received. Our team will review it and follow up by email.',
      });
      setFormData(INITIAL_FORM);
      setErrors({});
    } catch (error) {
      setSubmitState({
        type: 'error',
        message: error.message || 'Something went wrong while sending your message.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`info-page-shell ${isDark ? 'info-page-dark' : 'info-page-light'}`}>
      <div className="info-page-card">
        <div className="info-page-topbar">
          <div className="info-page-brand"><BrandWordmark className="info-page-brand-mark" /></div>
          <div className="info-page-topbar-actions">
            {toggleTheme ? (
              <button
                className="theme-toggle-btn info-page-theme-toggle"
                type="button"
                onClick={toggleTheme}
                aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
              >
                {isDark ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
              </button>
            ) : null}
            <button className="nav-primary-btn" type="button" onClick={() => navigate('/')}>
              Back to Home
            </button>
          </div>
        </div>

        <header className="info-page-header">
          <div className="info-page-badge">Contact</div>
          <h1>Get in Touch</h1>
          <p>We&apos;d love to hear from you. Whether you have a question, proposal, or partnership idea, drop us a line.</p>
        </header>

        <div className="info-page-body info-page-body-readable">
          <div className="contact-layout">
            <section className="info-page-form contact-form-card" aria-labelledby="contact-form-title">
              <div className="info-page-section-kicker">Send us a message</div>
              <h3 id="contact-form-title">Send us a Message</h3>
              <p className="contact-form-intro">
                Tell us a little about what you need and we&apos;ll route your message to the right team.
              </p>
              <form onSubmit={handleSubmit} noValidate>
                <div className="info-page-form-grid">
                  <div className="info-page-field">
                    <label htmlFor="contact-name">Full Name</label>
                    <input
                      id="contact-name"
                      type="text"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleChange('name')}
                      aria-invalid={Boolean(errors.name)}
                      aria-describedby={errors.name ? 'contact-name-error' : undefined}
                    />
                    {errors.name ? <p id="contact-name-error" className="info-page-field-error">{errors.name}</p> : null}
                  </div>
                  <div className="info-page-field">
                    <label htmlFor="contact-email">Email Address</label>
                    <input
                      id="contact-email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={handleChange('email')}
                      aria-invalid={Boolean(errors.email)}
                      aria-describedby={errors.email ? 'contact-email-error' : undefined}
                    />
                    {errors.email ? <p id="contact-email-error" className="info-page-field-error">{errors.email}</p> : null}
                  </div>
                  <div className="info-page-field info-page-field-full">
                    <label htmlFor="contact-reason">Reason for Contact</label>
                    <select
                      id="contact-reason"
                      value={formData.reason}
                      onChange={handleChange('reason')}
                      aria-invalid={Boolean(errors.reason)}
                      aria-describedby={errors.reason ? 'contact-reason-error' : 'contact-reason-help'}
                    >
                      <option value="" disabled>Select a reason...</option>
                      <option>Sales & Enterprise</option>
                      <option>Press & Media</option>
                      <option>General Inquiry</option>
                    </select>
                    <p id="contact-reason-help" className="info-page-field-help">
                      Messages for this category will go to <strong>{targetEmail}</strong>.
                    </p>
                    {errors.reason ? <p id="contact-reason-error" className="info-page-field-error">{errors.reason}</p> : null}
                  </div>
                  <div className="info-page-field info-page-field-full">
                    <label htmlFor="contact-subject">Subject</label>
                    <input
                      id="contact-subject"
                      type="text"
                      placeholder="What is your message about?"
                      value={formData.subject}
                      onChange={handleChange('subject')}
                      aria-invalid={Boolean(errors.subject)}
                      aria-describedby={errors.subject ? 'contact-subject-error' : undefined}
                    />
                    {errors.subject ? <p id="contact-subject-error" className="info-page-field-error">{errors.subject}</p> : null}
                  </div>
                  <div className="info-page-field info-page-field-full">
                    <label htmlFor="contact-message">Message</label>
                    <textarea
                      id="contact-message"
                      placeholder="Please provide as much detail as possible..."
                      value={formData.message}
                      onChange={handleChange('message')}
                      aria-invalid={Boolean(errors.message)}
                      aria-describedby={errors.message ? 'contact-message-error' : 'contact-message-help'}
                    />
                    <p id="contact-message-help" className="info-page-field-help">
                      Include organization, browser, and any deadlines if that helps us route your request faster.
                    </p>
                    {errors.message ? <p id="contact-message-error" className="info-page-field-error">{errors.message}</p> : null}
                  </div>
                </div>
                {submitState.message ? (
                  <div className={`info-page-status info-page-status-${submitState.type || 'info'}`} aria-live="polite">
                    {submitState.message}
                  </div>
                ) : null}
                <div className="info-page-action">
                  <button className="nav-primary-btn" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </div>
              </form>
            </section>

            <div className="contact-side">
              <section className="info-page-panel contact-panel">
                <div className="contact-panel-label">Sales</div>
                <h3>Sales &amp; Enterprise</h3>
                <p>Discuss licensing, partnerships, custom deployment, or large team onboarding.</p>
                <p><strong>info@redacto.net</strong></p>
              </section>
              <section className="info-page-panel contact-panel">
                <div className="contact-panel-label">Media</div>
                <h3>Press &amp; Media</h3>
                <p>For interviews, product mentions, and editorial requests.</p>
                <p><strong>info@redacto.net</strong></p>
              </section>
              <section className="info-page-panel contact-panel">
                <div className="contact-panel-label">General</div>
                <h3>General Inquiries</h3>
                <p>Use this mailbox for everything else related to the product or company.</p>
                <p><strong>info@redacto.net</strong></p>
              </section>
            </div>
          </div>
        </div>
      </div>
      <PublicSiteFooter />
    </div>
  );
}

export default Contact;
